import { clone, newDesignSettings } from './portfolioDesign';
import { SEARCHABLE_PORTFOLIO_RULES, attributeMetricByName, portfolioRuleId } from '../components/optimizer/optimizerRuleRegistry';
import { optimizerGroupIntentKey } from './optimizerGenericGroupContract';
import { MAX_SHARPE_OBJECTIVE, designForManualRun, manualDesign } from './optimizerObjectives';

export const MAX_SEARCH_JOBS = 64;

export function attributeSearchUnavailableReason(evaluation) {
    return evaluation?.identity?.member_count === 0 ? 'No selected securities in this category' : null;
}

export function autoUnavailableReason(parameter) {
    if (parameter?.startsWith('design:')) return 'Keep conviction floors Fixed';
    if (parameter?.startsWith('cardinality:')) return 'Keep names-changed limits Fixed';
    return null;
}

export function searchableConstraints(draft, evaluationIndex) {
    const records = SEARCHABLE_PORTFOLIO_RULES.map((rule) => ({
        parameter: portfolioRuleId(rule), label: rule.label, enabled: draft.constraintToggles.static_constraints[rule.key] === true,
    }));
    Object.values(draft.constraints.dynamic_constraints ?? {}).forEach((row) => records.push({
        parameter: `factor:${row.factor_name}`, label: row.factor_name, enabled: row.enabled,
    }));
    draft.groupConstraints.forEach((row) => {
        const evaluated = evaluationIndex.byGroupIntentKey.get(optimizerGroupIntentKey(row));
        records.push({ parameter: evaluated ? `group:${evaluated.rule_id}` : null,
            label: [evaluated?.identity?.attribute_name, evaluated?.identity?.category_label,
                attributeMetricByName(row.name)?.label].filter(Boolean).join(' · ') || 'Attribute constraint',
            unavailableReason: attributeSearchUnavailableReason(evaluated), enabled: row.enabled,
        });
    });
    const floor = draft.constraints.design?.mandate?.score_floor;
    if (floor) records.push({ parameter: floor.mode === 'retention_pct' ? 'design:retention_pct' : 'design:absolute_score',
        label: 'Alpha-Score floor', enabled: true,
    });
    return records;
}

export function prepareOptimizerSearch(draft, objective, ranges, options, evaluationIndex) {
    const next = clone(draft);
    const auto = [];
    for (const record of searchableConstraints(draft, evaluationIndex)) {
        if (!record.enabled || !ranges[record.parameter]) continue;
        if (record.unavailableReason) {
            throw new Error(`${record.label}: ${record.unavailableReason}. Select a security from this category, or set this limit to Fixed or Off.`);
        }
        const reason = autoUnavailableReason(record.parameter);
        if (reason) throw new Error(`${record.label}: ${reason}.`);
        auto.push(record.parameter);
    }
    if (auto.length > 24) throw new Error('Use Auto for at most 24 constraints in one search.');
    // Searches explore construction balances; Max Sharpe runs once on its own contract.
    if (auto.length && objective === MAX_SHARPE_OBJECTIVE) {
        throw new Error('Search is not available with Max Sharpe. Set each limit to Fixed or Off.');
    }
    if (auto.length && (!Number.isInteger(options.trial_budget) || options.trial_budget < 1 || options.trial_budget > MAX_SEARCH_JOBS)) {
        throw new Error(`Choose between 1 and ${MAX_SEARCH_JOBS} search runs.`);
    }
    const design = designForManualRun(objective, next.constraints) ?? manualDesign('AlphaScore');
    return { draft: next, settings: { ...newDesignSettings(), ...options, design, auto, search: [] }, isSearch: auto.length > 0 };
}
